import React, { useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { usePostHog } from "posthog-js/react";
import { logger } from "../../lib/logger";

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

// Component that uses Clerk hooks
const AnalyticsProviderWithClerk: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const { user, isLoaded } = useUser();
  const posthog = usePostHog();

  useEffect(() => {
    if (isLoaded && user && posthog) {
      // Identify user with PostHog
      const userProperties = {
        // Core properties
        $email: user.emailAddresses?.[0]?.emailAddress,
        $name: `${user.firstName || ""} ${user.lastName || ""}`.trim() || undefined,

        // ADHD Profile - will be set during onboarding
        subscription_tier: "free",
        primary_device: getPrimaryDevice(),

        // Metadata we can infer
        email_verified: user.emailAddresses[0]?.verification.status === "verified",
        signup_date: user.createdAt?.toISOString(),
        last_sign_in: user.lastSignInAt?.toISOString(),
        has_profile_image: !!user.imageUrl,

        // App metadata
        app_version: import.meta.env.VITE_VERSION || "1.0.0",
        platform: "web",
      };

      posthog.identify(user.id, userProperties);

      // Track sign-in event
      posthog.capture("user_signed_in", {
        method: "clerk",
        timestamp: new Date().toISOString(),
      });

      logger.info("User identified with PostHog", {
        userId: user.id,
        component: "AnalyticsProvider",
      });
    }
  }, [user, isLoaded, posthog]);

  return <>{children}</>;
};

// Component that doesn't use Clerk hooks (for development)
const AnalyticsProviderWithoutClerk: React.FC<AnalyticsProviderProps> = ({ children }) => {
  return <>{children}</>;
};

// Export the appropriate component based on environment
export const AnalyticsProvider =
  import.meta.env.VITE_DISABLE_CLERK === "true"
    ? AnalyticsProviderWithoutClerk
    : AnalyticsProviderWithClerk;

function getPrimaryDevice(): "mobile" | "desktop" | "tablet" {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/tablet|ipad|playbook|silk/.test(userAgent)) {
    return "tablet";
  }

  if (
    /mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/.test(
      userAgent
    )
  ) {
    return "mobile";
  }

  return "desktop";
}
